import { LngLatBounds } from "mapbox-gl";

export enum MapLoadingSource {
  XdaProjection = "xdaProjection",
  Bubble = "bubble",
  Completion = "completion",
  Coverage = "coverage",
  Shapefile = "shapefile",
  GeoModel = "geoModel",
  Filtering = "filtering",
  Facility = "facility",
  General = "general"
}

export type LoadingSources = {
  [key in MapLoadingSource]?: boolean;
};

export interface EntityGroup {
  id: number;
  title: string;
  color: string;
}

export interface EntitiesInGroup {
  uwis: string[];
  group: EntityGroup;
}

export type BaseLayerKind =
  | "Street"
  | "Satellite"
  | "Terrain"
  | "Grayscale"
  | "Dark"
  | "None";

export interface DefaultMapStyle {
  id?: string;
  style: BaseLayerKind;
}

export const DEFAULT_MAP_STYLE: BaseLayerKind = "Street";

export interface EntitiesInGroupModel {
  uwis: { [uwi: string]: string };
  group: EntityGroup;
}

export interface HighlightedFeatures {
  line: GeoJSON.Feature[];
  point: GeoJSON.Feature[];
  facility: GeoJSON.Feature[];
  /** Hovered well UWI → color. Absent for facility hovers. */
  uwis?: Record<string, string>;
}

export interface BubbleVisLegend {
  title: string;
  visible: boolean;
  minValue: number;
  maxValue: number;
  colors: { title: string; color: string }[];
}

export interface MapContextState {
  /**
   * This property manages the loading state for various things in relation to the map
   * It's computed from loadingSources - true if any source is loading
   * Linked to the horizontal loading bar above the map.
   */
  isLoading: boolean;
  /**
   * Individual loading sources tracked separately to prevent conflicts
   */
  loadingSources: LoadingSources;
  created: boolean;
  bounds: LngLatBounds;
  mapAlert: string;
  /**
   * features that the user has selected
   */
  selectedFeatures: GeoJSON.Feature[];
  /**
   * features that is higlighted on the map
   */
  highlightedFeatures: HighlightedFeatures;
  isStyleLoaded: boolean;
  mapbox: mapboxgl.Map;
  noWellsAvailable: boolean;
  isPolygonFilterControlVisible: boolean;
  entitiesInGroup: { [group: string]: EntitiesInGroupModel };
  facilitiesInGroup: { [group: string]: EntitiesInGroupModel };
  /**
   * Map is initialized when style has been loaded and wells are visible on the map
   */
  isMapInitialized: boolean;
  isFullscreen: boolean;
  /**
   * True only while the map is composing a screenshot. Distinct from
   * `isFullscreen`, which the expanded-map toggle and the geom-bin panel also
   * set: freezing the viewport-driven filter is correct for a screenshot and
   * wrong for those two.
   */
  isScreenshotMode: boolean;
  baseLayer: BaseLayerKind;
  bubbleVisLegend: BubbleVisLegend | undefined;
}

export type FullscreenState = {
  isFullscreen: boolean;
};

export type ScreenshotModeState = {
  isScreenshotMode: boolean;
};

export type SelectedFeaturesState = {
  selectedFeatures: GeoJSON.Feature[];
};

export type HighlightedFeaturesState = {
  highlightedFeatures: {
    line: GeoJSON.Feature[];
    point: GeoJSON.Feature[];
    facility: GeoJSON.Feature[];
  };
};

export type SelectedAndHighlightFeatureState = SelectedFeaturesState &
  HighlightedFeaturesState;

export type UpdateLoadingState = {
  isLoading?: boolean;
  loadingSource?: MapLoadingSource;
  loadingSources?: LoadingSources;
};

export type MapBoundState = {
  bounds: LngLatBounds;
};
export type StyleLoadedState = {
  isStyleLoaded: boolean;
};

export type NoWellsAvailableState = {
  noWellsAvailable: boolean;
};
export type CreatedState = {
  created: boolean;
};
export type MapboxglState = {
  mapbox: mapboxgl.Map;
};
export type MapInitializedState = {
  isMapInitialized: boolean;
};
export type MapAlertState = {
  mapAlert: string;
};
export type BaseLayerState = {
  baseLayer: BaseLayerKind;
};
export type BubbleVisLegendState = {
  bubbleVisLegend: BubbleVisLegend | undefined;
};
export type EntitiesInGroupState = {
  entitiesInGroup: { [group: string]: EntitiesInGroupModel };
};
export type FacilitiesInGroupState = {
  facilitiesInGroup: { [group: string]: EntitiesInGroupModel };
};
export type IsPolygonFilterControlVisibleState = {
  isPolygonFilterControlVisible: boolean;
};

export type SetHasLoadedShapefiles = {
  hasLoadedShapefiles: boolean;
};
